import type { Vec3 } from "../core";
import type { ProceduralActionPose } from "../motion";
import * as THREE from "three";
import type { Object3D } from "three";
import type { CharacterRig } from "./class-kits";

export const HUMANOID_BASIS = Object.freeze({
  right: Object.freeze({ x: 1, y: 0, z: 0 }),
  up: Object.freeze({ x: 0, y: 1, z: 0 }),
  forward: Object.freeze({ x: 0, y: 0, z: 1 }),
});

export interface HumanoidLegDimensions {
  hipY: number;
  upperLegLength: number;
  lowerLegLength: number;
}

export interface PlanarLegSolution {
  upperX: number;
  lowerX: number;
  footX: number;
}

export interface TwoBoneSolution {
  bend: Vec3;
  upperDirection: Vec3;
  lowerDirection: Vec3;
}

/**
 * Deterministic two-bone IK in three dimensions. The pole is a preferred
 * bend direction, not a second target: projecting it onto the target plane
 * keeps elbows readable while the endpoint remains exactly reachable. The
 * caller decides whether the result is used for a hand, foot, or future
 * equipment chain; this keeps the solver independent of any class recipe.
 */
export function solveTwoBone(
  origin: Vec3,
  target: Vec3,
  upperLength: number,
  lowerLength: number,
  pole: Vec3 = { x: 0, y: 0, z: 1 },
): TwoBoneSolution {
  const toTarget = new THREE.Vector3(target.x - origin.x, target.y - origin.y, target.z - origin.z);
  const minimum = Math.abs(upperLength - lowerLength) + 1e-5;
  const maximum = upperLength + lowerLength - 1e-5;
  const rawDistance = toTarget.length();
  const distance = Math.max(minimum, Math.min(maximum, rawDistance));
  const direction = rawDistance > 1e-5
    ? toTarget.clone().normalize()
    : new THREE.Vector3(0, -1, 0);

  const poleVector = new THREE.Vector3(pole.x, pole.y, pole.z);
  const projectedPole = poleVector.clone().sub(direction.clone().multiplyScalar(poleVector.dot(direction)));
  if (projectedPole.lengthSq() < 1e-8) {
    // A pole parallel to the target is under-specified. Pick a stable axis
    // that is least parallel to the target so the result never flips based on
    // floating-point noise at the exact front/back centerline.
    const fallback = Math.abs(direction.y) < 0.8
      ? new THREE.Vector3(0, 1, 0)
      : new THREE.Vector3(1, 0, 0);
    projectedPole.copy(fallback).sub(direction.clone().multiplyScalar(fallback.dot(direction)));
  }
  const bendDirection = projectedPole.normalize();
  const along = (distance * distance + upperLength * upperLength - lowerLength * lowerLength) / (2 * distance);
  const bendHeight = Math.sqrt(Math.max(0, upperLength * upperLength - along * along));
  const bend = new THREE.Vector3(origin.x, origin.y, origin.z)
    .add(direction.clone().multiplyScalar(along))
    .add(bendDirection.clone().multiplyScalar(bendHeight));
  const upperDirection = bend.clone().sub(new THREE.Vector3(origin.x, origin.y, origin.z)).normalize();
  const lowerDirection = new THREE.Vector3(target.x, target.y, target.z).sub(bend).normalize();
  return {
    bend: { x: bend.x, y: bend.y, z: bend.z },
    upperDirection: { x: upperDirection.x, y: upperDirection.y, z: upperDirection.z },
    lowerDirection: { x: lowerDirection.x, y: lowerDirection.y, z: lowerDirection.z },
  };
}

/** Apply a target to a jointed limb whose bones extend along local -Y. */
export function applyTwoBoneTarget(
  root: Object3D,
  mid: Object3D,
  target: Vec3,
  upperLength: number,
  lowerLength: number,
  pole: Vec3 = { x: 0, y: 0, z: 1 },
): TwoBoneSolution {
  const origin = { x: root.position.x, y: root.position.y, z: root.position.z };
  const solution = solveTwoBone(origin, target, upperLength, lowerLength, pole);
  const canonical = new THREE.Vector3(0, -1, 0);
  const upperQuaternion = new THREE.Quaternion().setFromUnitVectors(
    canonical,
    new THREE.Vector3(solution.upperDirection.x, solution.upperDirection.y, solution.upperDirection.z),
  );
  root.quaternion.copy(upperQuaternion);

  const lowerLocal = new THREE.Vector3(solution.lowerDirection.x, solution.lowerDirection.y, solution.lowerDirection.z)
    .applyQuaternion(upperQuaternion.clone().invert());
  mid.quaternion.setFromUnitVectors(canonical, lowerLocal.normalize());
  return solution;
}

/** Analytic two-bone IK in the humanoid's Y/Z sagittal plane. */
export function solvePlanarLeg(
  hip: Pick<Vec3, "y" | "z">,
  ankleTarget: Pick<Vec3, "y" | "z">,
  upperLength: number,
  lowerLength: number,
): PlanarLegSolution {
  const down = hip.y - ankleTarget.y;
  const forward = hip.z - ankleTarget.z;
  const minimum = Math.abs(upperLength - lowerLength) + 1e-5;
  const maximum = upperLength + lowerLength - 1e-5;
  const rawDistance = Math.hypot(down, forward);
  const distance = Math.max(minimum, Math.min(maximum, rawDistance));
  const kneeCos = clamp((distance * distance - upperLength * upperLength - lowerLength * lowerLength)
    / (2 * upperLength * lowerLength), -1, 1);
  const lowerX = Math.acos(kneeCos);
  const targetAngle = Math.atan2(forward, down);
  const upperX = targetAngle - Math.atan2(
    lowerLength * Math.sin(lowerX),
    upperLength + lowerLength * Math.cos(lowerX),
  );
  return { upperX, lowerX, footX: -(upperX + lowerX) };
}

/**
 * Plant both action feet at actor-local targets while the root translates.
 * The solve is visual-only and never feeds movement into the source game.
 */
export function applyActionFootPlacement(
  rig: CharacterRig,
  pose: ProceduralActionPose,
  dimensions: HumanoidLegDimensions,
  amount: number,
): void {
  applyFootTargets(rig, pose.leftFootTarget, pose.rightFootTarget, pose.root, dimensions, amount);
}

export function applyFootTargets(
  rig: CharacterRig,
  leftTarget: Vec3,
  rightTarget: Vec3,
  root: Vec3,
  dimensions: HumanoidLegDimensions,
  amount: number,
): void {
  plantLeg(rig.leftLeg, rig.leftShin, rig.leftFoot, leftTarget, root, dimensions, amount);
  plantLeg(rig.rightLeg, rig.rightShin, rig.rightFoot, rightTarget, root, dimensions, amount);
}

function plantLeg(
  upper: Object3D,
  lower: Object3D,
  foot: Object3D,
  target: Vec3,
  root: Vec3,
  dimensions: HumanoidLegDimensions,
  amount: number,
): void {
  const scaledRoot = { x: root.x * amount, y: root.y * amount, z: root.z * amount };
  const actorTarget = {
    x: upper.position.x + (target.x - upper.position.x) * amount,
    y: target.y,
    z: target.z * amount,
  };
  const localTarget = {
    x: actorTarget.x - scaledRoot.x,
    y: actorTarget.y - scaledRoot.y,
    z: actorTarget.z - scaledRoot.z,
  };
  const footYaw = foot.rotation.y;
  const footRoll = foot.rotation.z;
  applyTwoBoneTarget(
    upper,
    lower,
    localTarget,
    dimensions.upperLegLength,
    dimensions.lowerLegLength,
    { x: 0, y: 0, z: 1 },
  );

  // Keep the sole aligned to the actor's ground plane after the 3D leg solve,
  // then restore presentation-authored toe-out. This makes lateral weight
  // transfer visible without rolling the feet onto their edges.
  foot.quaternion.copy(upper.quaternion.clone().multiply(lower.quaternion).invert());
  foot.rotateY(footYaw);
  foot.rotateZ(footRoll);
}

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.max(minimum, Math.min(maximum, value));
}
